import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  OrphanedOrder,
  AgedClawback,
  OpenAdjustmentGroup,
  ClawbackFlag,
} from '../../services/settlementsApi';

// Spec 053 Flow D1 — settlement exceptions. A detector, not a second settlement path: it reports
// what the single collection rule should have wrapped but did not, and offers the existing
// "Run settlement now" (run-cycle) for the orphan's garage.

const aed = (n: number) => `AED ${n.toLocaleString('en-AE', { minimumFractionDigits: 2 })}`;
const age = (days: number) => `${days}d`;
const date = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '-');

const flagBadge = (flag: ClawbackFlag) =>
  flag === 'DECISION_REQUIRED'
    ? <span className="admin-badge rejected">Decision required</span>
    : flag === 'PROVISION'
      ? <span className="admin-badge pending">Provision</span>
      : null;

const ORPHAN_AGE_TIP = 'Days since the order became eligible for settlement. An order is an exception once it is older than two of its garage’s cycle lengths and still unsettled.';
const CLAWBACK_AGE_TIP = 'Days the debit has stayed open (unrecovered). 60 days = Provision (month-end pack); 90 days = an admin decision is required: write off or block the garage.';
const ADJUSTMENT_AGE_TIP = 'Days since the adjustment was created and not yet applied to a paid settlement.';

export interface ExceptionsData {
  orphans: OrphanedOrder[];
  clawbacks: AgedClawback[];
  adjustments: OpenAdjustmentGroup[];
}

export const countExceptions = (d: ExceptionsData | null) =>
  d ? d.orphans.length + d.clawbacks.length + d.adjustments.reduce((n, g) => n + g.items.length, 0) : 0;

interface Props {
  data: ExceptionsData | null;
  loading: boolean;
  error: string | null;
  /**
   * #520 — why the last "Run settlement now" produced nothing (period already settled, nothing
   * eligible). A 202 that generated nothing is not an error, so it must not use the red banner,
   * but it must be said: the button used to answer silently.
   */
  notice?: string | null;
  /** garageId currently being settled via "Run settlement now"; disables the other buttons. */
  running: string | null;
  onRunSettlement: (garageId: string) => void;
}

const EmptyState = () => (
  <div className="admin-empty-state" style={{ padding: '1rem' }}><p>No exceptions</p></div>
);

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <section className="admin-card" style={{ marginBottom: '1rem' }} aria-label={title}>
      <h3 className="admin-card-title" style={{ marginBottom: '0.75rem' }}>
        {title} ({count})
      </h3>
      {children}
    </section>
  );
}

/**
 * #520 — rendered ABOVE the loading / empty early returns on purpose. A run that refuses because the
 * period is already settled also empties the orphan list it was launched from, so a notice rendered
 * only in the "we have data" branch unmounts in the same tick it is set — reinstating the silent
 * no-op this ticket exists to kill.
 */
const RunCycleNotice = ({ notice }: { notice: string | null }) =>
  notice ? (
    <div className="admin-info-banner info" role="status" data-testid="run-cycle-notice" style={{ marginBottom: '0.75rem' }}>
      {notice}
    </div>
  ) : null;

export default function ExceptionsTab({ data, loading, error, notice = null, running, onRunSettlement }: Props) {
  if (loading && !data) {
    return (
      <div>
        <RunCycleNotice notice={notice} />
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        <RunCycleNotice notice={notice} />
        <div className="admin-empty-state"><p>{error ?? 'No exceptions'}</p></div>
      </div>
    );
  }

  const { orphans, clawbacks, adjustments } = data;

  return (
    <div>
      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      <RunCycleNotice notice={notice} />

      {/* BR-12 / AC-18 — orphaned orders */}
      <Section title="Orphaned orders" count={orphans.length}>
        {orphans.length === 0 ? <EmptyState /> : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Garage</th>
                  <th>Eligible since</th>
                  <th title={ORPHAN_AGE_TIP}>Age &#9432;</th>
                  <th>Cycle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((o) => (
                  <tr key={o.orderId}>
                    <td style={{ fontWeight: 500 }}>
                      <Link to={`/orders/${o.orderId}`} className="admin-link">
                        {o.orderNumber || o.orderId.slice(0, 8)}
                      </Link>
                      {o.reasonHint && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>{o.reasonHint}</div>
                      )}
                    </td>
                    <td>
                      <Link to={`/garages/${o.garageId}`} className="admin-link">
                        {o.garageName || o.garageId.slice(0, 8)}
                      </Link>
                    </td>
                    <td>{date(o.eligibleAt)}</td>
                    <td>{age(o.ageDays)}</td>
                    <td>{o.cycle ?? '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-outline admin-btn-sm"
                        disabled={running !== null}
                        onClick={() => onRunSettlement(o.garageId)}
                      >
                        {running === o.garageId ? 'Running…' : 'Run settlement now'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* BR-13 / AC-19 / AC-21d — aged clawbacks */}
      <Section title="Aged clawbacks" count={clawbacks.length}>
        {clawbacks.length === 0 ? <EmptyState /> : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Garage</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Source</th>
                  <th title={CLAWBACK_AGE_TIP}>Age &#9432;</th>
                  <th>Flag</th>
                  <th>Originating</th>
                </tr>
              </thead>
              <tbody>
                {clawbacks.map((c) => (
                  <tr key={c.adjustmentId}>
                    <td style={{ fontWeight: 500 }}>
                      <Link to={`/garages/${c.garageId}`} className="admin-link">
                        {c.garageName || c.garageId.slice(0, 8)}
                      </Link>
                    </td>
                    <td style={{ textAlign: 'right' }}>{aed(c.amount)}</td>
                    <td>{c.source ?? '-'}</td>
                    <td>{age(c.ageDays)}</td>
                    <td>{flagBadge(c.flag) ?? '-'}</td>
                    <td>
                      {c.refOrderId ? (
                        <Link to={`/orders/${c.refOrderId}`} className="admin-link">
                          {c.refOrderNumber || 'Order'}
                        </Link>
                      ) : (c.refOrderNumber ?? null)}
                      {c.refRefundId && (
                        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                          refund {c.refRefundId}
                        </div>
                      )}
                      {!c.refOrderId && !c.refOrderNumber && !c.refRefundId && '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* AC-20 — open adjustments grouped by garage */}
      <Section title="Open adjustments" count={adjustments.reduce((n, g) => n + g.items.length, 0)}>
        {adjustments.length === 0 ? <EmptyState /> : adjustments.map((g) => (
          <div key={g.garageId} style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, color: '#030c54', marginBottom: '0.5rem' }}>
              <Link to={`/garages/${g.garageId}`} className="admin-link">
                {g.garageName || g.garageId.slice(0, 8)}
              </Link>
              <span style={{ fontWeight: 400, color: 'var(--admin-text-muted)', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                {g.items.length} open
              </span>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Source</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Reason</th>
                    <th title={ADJUSTMENT_AGE_TIP}>Age &#9432;</th>
                    <th>Originating</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <span className={`admin-badge ${a.type === 'DEBIT' ? 'rejected' : 'approved'}`}>{a.type}</span>
                      </td>
                      <td>{a.source ?? '-'}</td>
                      <td style={{ textAlign: 'right' }}>{aed(a.amount)}</td>
                      <td>{a.reason ?? '-'}</td>
                      <td>{age(a.ageDays)}</td>
                      <td>
                        {a.refs?.originSettlementId && (
                          <Link to={`/settlements/${a.refs.originSettlementId}`} className="admin-link" style={{ marginRight: '0.5rem' }}>
                            {a.refs.originSettlementNumber || 'Settlement'}
                          </Link>
                        )}
                        {a.refs?.orderId && (
                          <Link to={`/orders/${a.refs.orderId}`} className="admin-link">
                            {a.refs.orderNumber || 'Order'}
                          </Link>
                        )}
                        {a.refs?.refundId && (
                          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                            refund {a.refs.refundId}
                          </div>
                        )}
                        {!a.refs?.originSettlementId && !a.refs?.orderId && !a.refs?.refundId && '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}
